import Button from "../../../../../../components/base/button/button";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Card, Fade, Paper, Skeleton, Typography} from "@mui/material";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from "../../../../../../components/form";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import Grid from "@mui/material/Unstable_Grid2";
import classnames from "classnames";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import IconButton from "../../../../../../components/base/button/icon_button";
import {DeleteOutlined, EditOutlined} from "@mui/icons-material";

const NCRNoteTitleSuggestionsSetting = () => {
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();

    /**
     * Fetches the entrys of the leads in the system.
     */
    const getList = useCallback(async () => {
        setLoading(true);
        const response = await Api.getNcrNoteTitleSuggestions();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setData(response.data?.map(e => ({
                ...e,
                inEditMode: false,
                editing: false,
            })) ?? []);
        }
    }, [isMounted]);

    /**
     * As soon as the dialog opens:
     * - fetches the list of entrys of leads
     */
    useEffect(() => {
        getList().then();
    }, [getList]);

    const schema = useMemo(() => Yup.object({
        title: Yup.string()
            .required(ValidateMessages.required)
            .trim(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Submits the form values to create a new entry
     */
    const createEntry = useCallback(async (values, form) => {
        setCreating(true);
        const response = await Api.createNcrNoteTitleSuggestion({
            title: values.title,
        });
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag && !response.aborted) {
            setData(prevState => [
                ...prevState,
                {
                    ...response.data,
                    inEditMode: false,
                    editing: false
                }
            ]);
            form.change('title', '')
            form.resetFieldState('title');
        }
    }, [isMounted, setData]);

    /**
     * Updates an entry with the given values.
     *
     * @param entry the entry to be updated.
     * @return {Promise<boolean>}
     */
    const updateEntry = useCallback(async (entry) => {
        setData(prevState => prevState.map(e =>
            e.id === entry.id
                ? {...e, editing: true}
                : e
        ));
        const response = await Api.updateNcrNoteTitleSuggestion({
            id: entry.id,
            title: entry.title,
        })
        if (!isMounted())
            return false
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: false} : e));
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === entry.id ? ({
                ...e,
                title: entry.title,
                inEditMode: false,
            }) : e) ?? []);
        }
        return response?.resultFlag ?? false
    }, [isMounted, setData])


    /**
     * Removes an entry
     * @param entry the entry to be removed
     */
    const removeEntry = useCallback(async (entry) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `The Note Title suggestion: "${entry.title}"`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                setData(prevState => prevState.map(e => e.id === entry.id
                    ? {...e, editing: true}
                    : e
                ));
                const response = await Api.deleteNcrNoteTitleSuggestion(entry.id)
                if (!isMounted())
                    return false
                if (response?.resultFlag) {
                    setData(prevState => prevState.filter(e => e.id !== entry.id));
                } else {
                    setData(prevState => prevState.map(e => e.id === entry.id ? ({...e, editing: false}) : e));
                }
                return response?.resultFlag ?? false
            }
        }));
    }, [dispatch, isMounted, setData]);
    /**
     * Toggles the edit mode of the entry.
     */
    const toggleInEditMode = useCallback((entry, inEditMode = false) => {
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, inEditMode} : e))
    }, [setData])

    /**
     * Creates the styles for the container boxes.
     * @param {Theme} theme                      The theme to use
     * @returns {ReturnType<ThemeMixin<Theme>>}  The styles for the container boxes
     */
    const boxMixin = useCallback((theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        borderRadius: theme.vars?.shape.borderRadius,
        padding: 2,
        marginBlock: 2,
    }), []);

    return (
        <>
            <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column'}}>
                <Form onSubmit={createEntry} validate={validate}>
                    <Box sx={boxMixin}>
                        <Typography variant={'subtitle2'} fontWeight={600}>
                            Create a new title suggestion
                        </Typography>
                        <Grid container xs={12} gap={2} spacing={2} marginInline={0} marginBlock={2}>
                            <Grid xs padding={0}>
                                <MuiRFF.TextField
                                    fullWidth
                                    size={'small'}
                                    name={"title"}
                                    label={"Title"}
                                    required={required.title}
                                />
                            </Grid>
                            <Grid xs={'auto'} padding={0}>
                                <Button
                                    variant={'contained'}
                                    type={'submit'}
                                    color={'primary'}
                                    size={'large'}
                                    disabled={creating}
                                    sx={{height: 37}}
                                >
                                    {
                                        creating
                                            ? "Creating..."
                                            : "Create"
                                    }
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Form>
                {
                    !loading &&
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginBlock: 1}}>
                        {
                            data?.map((entry) => (
                                <NCRNoteTitleSuggestionsSettingCard
                                    key={entry.id}
                                    entry={entry}
                                    update={updateEntry}
                                    remove={removeEntry}
                                    // removable={array.length > 1}
                                    removable
                                    setInEditMode={toggleInEditMode}
                                />
                            ))
                        }
                    </Box>
                }
                {
                    loading &&
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginBlock: 1}}>
                        {
                            Array.from({length: 4}).map((_, key) => (
                                <Skeleton
                                    component={'div'}
                                    key={key}
                                    width={'100%'}
                                    height={75}
                                    sx={{transformOrigin: 'none', transform: 'none'}}
                                />
                            ))
                        }
                    </Box>
                }
                {
                    !loading && data.length < 1 &&
                    <Typography variant={'caption'} color={'text.secondary'} textAlign={'center'} sx={{marginBlock: 6, width: '100%'}}>
                        No title suggestions found.
                    </Typography>
                }
            </Paper>
        </>
    )
}


export default NCRNoteTitleSuggestionsSetting;

const NCRNoteTitleSuggestionsSettingCard = ({
                                                update,
                                                remove,
                                                setInEditMode,
                                                ...props
                                            }) => {
    const schema = useMemo(() => Yup.object({
        title: Yup.string()
            .required(ValidateMessages.required)
            .trim(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        title: props.entry.title,
    }), [props.entry.title])

    /**
     * Updates the entry by calling the appropriate callback in the props.
     */
    const updateEntry = useCallback(async (values) => {
        if (props.entry.title === values.title) {
            return setInEditMode(props.entry, false);
        }
        await update({...props.entry, title: values.title,});
    }, [props.entry, update, setInEditMode])

    /**
     * Removes the entry by calling the appropriate callback in the props.
     */
    const removeEntry = useCallback(async () => {
        await remove(props.entry)
    }, [remove, props.entry])

    /**
     * Set the entry as in edit mode.
     */
    const setAsEditing = useCallback(() => {
        setInEditMode(props.entry, true);
    }, [setInEditMode, props.entry])

    /**
     * Rolls back the values of the form to its initial state.
     */
    const rollbackValues = useCallback((form) => {
        setInEditMode(props.entry, false);
        form.reset(initialValues);
    }, [initialValues, props.entry, setInEditMode]);

    return (
        <>
            <Card variant={'outlined'} sx={{paddingInline: 2}}>
                <Form
                    onSubmit={updateEntry}
                    validate={validate}
                    initialValues={initialValues}
                >
                    {({form}) => (
                        <Grid container xs={12} gap={1} spacing={2} marginInline={0} marginBlock={2}>
                            <Grid xs padding={0}>
                                <MuiRFF.TextField
                                    fullWidth
                                    size={'small'}
                                    name={"title"}
                                    label={"Title"}
                                    required={required.title}
                                    className={classnames({'show-as-text': !props.entry.inEditMode})}
                                />
                            </Grid>
                            <Grid xs={'auto'} padding={0}>
                                {
                                    !props.entry.inEditMode &&
                                    <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5}}>
                                        <Fade in={props.removable}>
                                            <IconButton
                                                disabled={!props.removable}
                                                onPressed={removeEntry}
                                                color={'paper.error'}
                                            >
                                                <DeleteOutlined/>
                                            </IconButton>
                                        </Fade>
                                        <IconButton onPressed={setAsEditing} color={'paper.primary'}>
                                            <EditOutlined/>
                                        </IconButton>
                                    </Box>
                                }
                                {
                                    props.entry.inEditMode &&
                                    <SaveOrRollback
                                        show
                                        column={false}
                                        showText={false}
                                        onSave={() => form.submit()}
                                        onRollback={() => rollbackValues(form)}
                                        loading={props.entry.editing}
                                    />
                                }
                            </Grid>
                        </Grid>
                    )}
                </Form>
            </Card>
        </>
    );
}
